import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useFamilyStore } from '../../store/familyStore';
import { useTodoStore } from '../../store/todoStore';
import { useThemeStore } from '../../store/themeStore';
import { useToastStore } from '../../store/toastStore';
import { getDesignTokens } from '../../utils/designTokens';
import { Todo, TodoList, TodoPriority, TodoStatus } from '../../types/todo';
import { EventMember } from '../../types/calendar';
import BottomSheet from '../../components/mobile/BottomSheet';
import { Icon } from '../../utils/icons';
import MemberAvatar from '../../components/MemberAvatar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { decodeJWT } from '../../utils/jwt';

export default function BetaTodos() {
  const navigate = useNavigate();
  const { isAuthenticated, idToken } = useAuthStore();
  const { family, fetchFamily } = useFamilyStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast } = useToastStore();
  const {
    todos,
    lists,
    isLoading,
    fetchTodos,
    fetchLists,
    createTodo,
    updateTodo,
    deleteTodo,
    createList,
    updateList,
    deleteList,
  } = useTodoStore();

  const [visibleCount, setVisibleCount] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState<string | 'all'>('all');
  const [listFilter, setListFilter] = useState<string | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TodoStatus | null>('open');
  
  // Get current user ID from JWT token
  const getCurrentUserId = (): string | null => {
    if (!idToken) return null;
    try {
      const decoded = decodeJWT(idToken);
      return decoded?.sub || null;
    } catch {
      return null;
    }
  };
  
  const currentUserId = getCurrentUserId();
  const [showLists, setShowLists] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    title?: string;
    description?: string;
    priority?: TodoPriority;
    status?: TodoStatus;
    assigneeId?: string;
    assigneeName?: string;
    listId?: string;
    dueDateTime?: Date | string;
    eventId?: string;
    eventTitle?: string;
  }>({});
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [swipeOffsets, setSwipeOffsets] = useState<Record<string, number>>({});
  const [swipeStartX, setSwipeStartX] = useState<Record<string, number>>({});
  const [isSwiping, setIsSwiping] = useState<Record<string, boolean>>({});
  const swipeStartY = useRef<Record<string, number>>({});

  // List management state
  const [showListManagement, setShowListManagement] = useState(false);
  const [showCreateListSheet, setShowCreateListSheet] = useState(false);
  const [showEditListSheet, setShowEditListSheet] = useState(false);
  const [editingList, setEditingList] = useState<TodoList | null>(null);
  const [newListName, setNewListName] = useState('');
  const [newListIcon, setNewListIcon] = useState('');
  const [editListName, setEditListName] = useState('');
  const [editListIcon, setEditListIcon] = useState('');

  // Create todo state
  const [showCreateTodoSheet, setShowCreateTodoSheet] = useState(false);
  const [isCreatingTodo, setIsCreatingTodo] = useState(false);
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [newTodoDescription, setNewTodoDescription] = useState('');
  const [newTodoDueDate, setNewTodoDueDate] = useState<string>('');
  const [newTodoPriority, setNewTodoPriority] = useState<TodoPriority>('medium');
  const [newTodoAssignee, setNewTodoAssignee] = useState<string>('family');
  const [newTodoListId, setNewTodoListId] = useState<string>('');

  // Loading states
  const [isUpdatingTodo, setIsUpdatingTodo] = useState(false);
  const [isDeletingTodo, setIsDeletingTodo] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [isUpdatingList, setIsUpdatingList] = useState(false);
  const [isDeletingList, setIsDeletingList] = useState(false);
  const [filterMessage, setFilterMessage] = useState<string | null>(null);
  const filterMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevStatusFilter = useRef<TodoStatus | null>(null);
  const prevPriorityFilter = useRef<TodoPriority | null>(null);
  const prevAssigneeFilter = useRef<string | 'all'>('all');
  const prevListFilter = useRef<string | 'all'>('all');

  useEffect(() => {
    if (!isAuthenticated || !idToken) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, idToken, navigate]);

  useEffect(() => {
    if (!family) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
    }
  }, [family, fetchFamily, showToast]);

  useEffect(() => {
    fetchLists().catch((error) => {
      console.error('Error fetching lists:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load lists', 'error');
    });
  }, [fetchLists, showToast]);

  useEffect(() => {
    fetchTodos({ scope: 'family' }).catch((error) => {
      console.error('Error fetching todos:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
    });
  }, [fetchTodos, showToast]);

  // Set initial filters: current user and open status
  useEffect(() => {
    if (currentUserId && assigneeFilter === 'all') {
      setAssigneeFilter(currentUserId);
    }
    if (statusFilter === null) {
      setStatusFilter('open');
    }
  }, [currentUserId]); // Only run when currentUserId changes

  // Get family members
  const familyMembers: EventMember[] = family?.members.map(m => ({
    memberId: m.userId,
    memberName: m.name,
    memberColor: m.color || '#3b82f6',
    memberAvatar: m.avatar,
  })) || [];

  // Show filter message only when filters are deselected (unclicked)
  useEffect(() => {
    // Skip on initial mount (when all prev values are at their initial state)
    if (
      prevStatusFilter.current === null &&
      prevPriorityFilter.current === null &&
      prevAssigneeFilter.current === 'all' &&
      prevListFilter.current === 'all'
    ) {
      // Update previous values and return early
      prevStatusFilter.current = statusFilter;
      prevPriorityFilter.current = priorityFilter;
      prevAssigneeFilter.current = assigneeFilter;
      prevListFilter.current = listFilter;
      return;
    }

    // Check if any filter was deselected (went from selected to unselected)
    const statusDeselected = prevStatusFilter.current !== null && statusFilter === null;
    const priorityDeselected = prevPriorityFilter.current !== null && priorityFilter === null;
    const assigneeDeselected = prevAssigneeFilter.current !== 'all' && assigneeFilter === 'all';
    const listDeselected = prevListFilter.current !== 'all' && listFilter === 'all';

    // Create a unique key for the current filter state
    const currentStateKey = `${statusFilter}-${priorityFilter}-${assigneeFilter}-${listFilter}`;

    // Only show message if a filter was deselected
    if (statusDeselected || priorityDeselected || assigneeDeselected || listDeselected) {
      // Clear existing timeout
      if (filterMessageTimeoutRef.current) {
        clearTimeout(filterMessageTimeoutRef.current);
      }

      // Build filter message based on current state
      const activeFilters: string[] = [];
      if (statusFilter) {
        activeFilters.push(statusFilter);
      }
      if (priorityFilter) {
        activeFilters.push(priorityFilter);
      }
      if (assigneeFilter !== 'all') {
        const member = familyMembers.find(m => m.memberId === assigneeFilter);
        if (member) {
          activeFilters.push(member.memberName);
        }
      }
      if (listFilter !== 'all') {
        const list = lists.find(l => l.listId === listFilter);
        if (list) {
          activeFilters.push(list.name);
        }
      }

      let message = '';
      if (activeFilters.length === 0) {
        message = 'Showing all';
      } else if (activeFilters.length === 1) {
        const filter = activeFilters[0];
        if (filter === 'open' || filter === 'done') {
          message = `Showing ${filter} only`;
        } else if (filter === 'critical' || filter === 'high' || filter === 'medium' || filter === 'low') {
          message = `Showing ${filter} priority only`;
        } else {
          message = `Showing ${filter} only`;
        }
      } else {
        message = `Showing ${activeFilters.length} filters`;
      }

      // Show message
      setFilterMessage(message);

      // Hide after 2 seconds
      filterMessageTimeoutRef.current = setTimeout(() => {
        setFilterMessage(null);
      }, 2000);
    }

    // Update previous values
    prevStatusFilter.current = statusFilter;
    prevPriorityFilter.current = priorityFilter;
    prevAssigneeFilter.current = assigneeFilter;
    prevListFilter.current = listFilter;

    return () => {
      if (filterMessageTimeoutRef.current) {
        clearTimeout(filterMessageTimeoutRef.current);
      }
    };
    // Only depend on filter values, not derived arrays that change on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, assigneeFilter, listFilter]);

  // Lists with todo counts
  const listsWithCounts = lists.map(list => ({
    ...list,
    count: todos.filter(t => t.listId === list.listId).length,
  }));

  // Filter todos
  const filtered = todos.filter(t => {
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (priorityFilter !== null && t.priority !== priorityFilter) return false;
    if (assigneeFilter !== 'all' && t.assigneeId !== assigneeFilter) return false;
    if (listFilter !== 'all' && t.listId !== listFilter) return false;
    if (statusFilter !== null && t.status !== statusFilter) return false;
    return true;
  });

  // Sort by priority (criticality) first, then by date
  const priorityOrder: Record<TodoPriority, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };

  const sortedAndFiltered = [...filtered].sort((a, b) => {
    // First sort by priority
    const aPriority = a.priority || 'medium';
    const bPriority = b.priority || 'medium';
    const priorityDiff = priorityOrder[aPriority] - priorityOrder[bPriority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then sort by date (earlier dates first)
    const aDate = a.dueDateTime ? new Date(a.dueDateTime).getTime() : Number.MAX_SAFE_INTEGER;
    const bDate = b.dueDateTime ? new Date(b.dueDateTime).getTime() : Number.MAX_SAFE_INTEGER;
    return aDate - bDate;
  });

  const visibleTodos = sortedAndFiltered.slice(0, visibleCount);
  const hasMore = sortedAndFiltered.length > visibleCount;

  // Swipe handlers with gesture detection
  const handleTouchStart = (todoId: string, e: React.TouchEvent) => {
    const touch = e.touches[0];
    setSwipeStartX(prev => ({ ...prev, [todoId]: touch.clientX }));
    swipeStartY.current[todoId] = touch.clientY;
    setIsSwiping(prev => ({ ...prev, [todoId]: true }));
  };

  const handleTouchMove = (todoId: string, e: React.TouchEvent) => {
    if (!isSwiping[todoId]) return;
    const touch = e.touches[0];
    const startX = swipeStartX[todoId] || 0;
    const startY = swipeStartY.current[todoId] || 0;
    const deltaX = touch.clientX - startX;
    const deltaY = Math.abs(touch.clientY - startY);
    
    // Lower threshold for faster response (5px instead of 10px)
    // Only trigger swipe if horizontal movement is greater than vertical (horizontal gesture)
    if (Math.abs(deltaX) > 5) {
      const todo = todos.find(t => t.todoId === todoId);
      if (todo) {
        // Prevent swiping in direction with no action
        // If todo is done, only allow swiping left (reopen)
        // If todo is open, only allow swiping right (mark done)
        if (todo.status === 'done' && deltaX > 0) {
          // Trying to swipe right on done todo - not allowed
          return;
        }
        if (todo.status === 'open' && deltaX < 0) {
          // Trying to swipe left on open todo - not allowed
          return;
        }
      }
      
      // If horizontal movement is clearly dominant, prevent scroll and start swipe
      if (Math.abs(deltaX) > deltaY * 1.5) {
        e.preventDefault(); // Prevent scroll during horizontal swipe
        e.stopPropagation(); // Stop event from bubbling
        
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          setSwipeOffsets(prev => ({ ...prev, [todoId]: deltaX }));
        });
      } else if (deltaY > Math.abs(deltaX) * 1.5) {
        // Vertical scroll detected, cancel swipe
        setIsSwiping(prev => {
          const newSwiping = { ...prev };
          delete newSwiping[todoId];
          return newSwiping;
        });
        setSwipeOffsets(prev => {
          const newOffsets = { ...prev };
          delete newOffsets[todoId];
          return newOffsets;
        });
      } else {
        // Ambiguous - prevent default to avoid horizontal scroll bar
        if (Math.abs(deltaX) > 5) {
          e.preventDefault();
        }
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          setSwipeOffsets(prev => ({ ...prev, [todoId]: deltaX }));
        });
      }
    }
  };

  const handleTouchEnd = async (todoId: string, e?: React.TouchEvent) => {
    const offset = swipeOffsets[todoId] || 0;
    const todo = todos.find(t => t.todoId === todoId);

    if (Math.abs(offset) > 100 && todo) {
      // Swipe right to mark done, swipe left to mark open
      const newStatus: TodoStatus = offset > 0 && todo.status === 'open' ? 'done' : 'open';
      if (newStatus !== todo.status) {
        try {
          await updateTodo(todoId, { status: newStatus });
          showToast(newStatus === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
        } catch (error) {
          console.error('Error updating todo status:', error);
          showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
        }
      }
    }

    setSwipeOffsets(prev => {
      const newOffsets = { ...prev };
      delete newOffsets[todoId];
      return newOffsets;
    });
    setIsSwiping(prev => {
      const newSwiping = { ...prev };
      delete newSwiping[todoId];
      return newSwiping;
    });
    setSwipeStartX(prev => {
      const newStartX = { ...prev };
      delete newStartX[todoId];
      return newStartX;
    });
    delete swipeStartY.current[todoId];
  };

  // Confirm field edit function
  const confirmFieldEdit = async (field: string, value?: any) => {
    if (!selectedTodo) return;

    const updateInput: any = {};

    if (field === 'title' && (value !== undefined || editValues.title !== undefined)) {
      updateInput.title = value !== undefined ? value : editValues.title;
    } else if (field === 'description' && (value !== undefined || editValues.description !== undefined)) {
      updateInput.description = value !== undefined ? value : editValues.description;
    } else if (field === 'priority' && (value || editValues.priority)) {
      updateInput.priority = value || editValues.priority;
    } else if (field === 'status' && (value || editValues.status)) {
      updateInput.status = value || editValues.status;
    } else if (field === 'assignee' && (value !== undefined || editValues.assigneeId !== undefined)) {
      const assigneeId = value !== undefined ? value : editValues.assigneeId;
      if (assigneeId === 'family') {
        updateInput.isFamily = true;
        updateInput.assigneeId = undefined;
      } else {
        updateInput.isFamily = false;
        updateInput.assigneeId = assigneeId;
        const member = familyMembers.find(m => m.memberId === assigneeId);
        // assigneeName is not sent - backend will derive it
      }
    } else if (field === 'list' && (value !== undefined || editValues.listId !== undefined)) {
      updateInput.listId = value !== undefined ? (value || undefined) : (editValues.listId || undefined);
    } else if (field === 'dueDate') {
      if (editValues.dueDateTime === undefined) {
        // Keep existing value - don't update
        return;
      } else if (editValues.dueDateTime === null || editValues.dueDateTime === '') {
        updateInput.dueDateTime = null;
      } else {
        updateInput.dueDateTime = editValues.dueDateTime instanceof Date ? editValues.dueDateTime : new Date(editValues.dueDateTime);
      }
    } else if (field === 'event' && editValues.eventId !== undefined) {
      updateInput.eventId = editValues.eventId || undefined;
      updateInput.eventTitle = editValues.eventTitle || undefined;
    }

    setIsUpdatingTodo(true);
    try {
      await updateTodo(selectedTodo.todoId, updateInput);
      setEditingField(null);
      setEditValues({});
      // Refresh todos to ensure UI updates with new list assignment
      await fetchTodos({ scope: 'family' });
      showToast('Todo updated', 'success');
    } catch (error) {
      console.error('Error updating todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
    } finally {
      setIsUpdatingTodo(false);
    }
  };

  // Delete todo function
  const handleDeleteTodo = async (todoId: string) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    setIsDeletingTodo(true);
    try {
      await deleteTodo(todoId);
      setShowDetailSheet(false);
      setSelectedTodo(null);
      showToast('Todo deleted', 'success');
    } catch (error) {
      console.error('Error deleting todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to delete todo', 'error');
    } finally {
      setIsDeletingTodo(false);
    }
  };

  // Create todo function
  const handleCreateTodo = async () => {
    if (!newTodoTitle.trim()) {
      showToast('Title is required', 'error');
      return;
    }

    setIsCreatingTodo(true);
    try {
      let dueDateTime: Date | undefined;
      if (newTodoDueDate) {
        const [year, month, day] = newTodoDueDate.split('-').map(Number);
        dueDateTime = new Date(Date.UTC(year, month - 1, day));
      }

      const assigneeId = newTodoAssignee === 'family' ? undefined : newTodoAssignee;
      const isFamily = newTodoAssignee === 'family';

      await createTodo({
        title: newTodoTitle.trim(),
        description: newTodoDescription.trim() || undefined,
        dueDateTime,
        priority: newTodoPriority,
        assigneeId,
        isFamily,
        listId: newTodoListId || undefined,
      });

      showToast('Todo created', 'success');
      setNewTodoTitle('');
      setNewTodoDescription('');
      setNewTodoDueDate('');
      setNewTodoPriority('medium');
      setNewTodoAssignee('family');
      setNewTodoListId('');
      setShowCreateTodoSheet(false);
      await fetchTodos({ scope: 'family' });
    } catch (error) {
      console.error('Error creating todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to create todo', 'error');
    } finally {
      setIsCreatingTodo(false);
    }
  };

  // List management functions
  const handleCreateList = async () => {
    if (!newListName.trim()) {
      showToast('List name is required', 'error');
      return;
    }

    setIsCreatingList(true);
    try {
      await createList({
        name: newListName.trim(),
        icon: newListIcon || '📋',
      });
      setNewListName('');
      setNewListIcon('');
      setShowCreateListSheet(false);
      // Refresh lists to ensure UI updates
      await fetchLists();
      // Close list management if open to show updated lists
      setShowListManagement(false);
      showToast('List created', 'success');
    } catch (error) {
      console.error('Error creating list:', error);
      showToast(error instanceof Error ? error.message : 'Failed to create list', 'error');
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleUpdateList = async () => {
    if (!editingList || !editListName.trim()) {
      showToast('List name is required', 'error');
      return;
    }

    setIsUpdatingList(true);
    try {
      await updateList(editingList.listId, {
        name: editListName.trim(),
        icon: editListIcon || editingList.icon || '📋',
      });
      setShowEditListSheet(false);
      setEditingList(null);
      setEditListName('');
      setEditListIcon('');
      // Refresh lists to ensure UI updates
      await fetchLists();
      // Close list management if open to show updated lists
      setShowListManagement(false);
      showToast('List updated', 'success');
    } catch (error) {
      console.error('Error updating list:', error);
      showToast(error instanceof Error ? error.message : 'Failed to update list', 'error');
    } finally {
      setIsUpdatingList(false);
    }
  };

  const handleDeleteList = async (listId: string) => {
    if (!window.confirm('Are you sure you want to delete this list? Todos in this list will not be deleted.')) return;

    setIsDeletingList(true);
    try {
      await deleteList(listId);
      // Refresh lists to ensure UI updates
      await fetchLists();
      // Close list management if open to show updated lists
      setShowListManagement(false);
      showToast('List deleted', 'success');
    } catch (error) {
      console.error('Error deleting list:', error);
      showToast(error instanceof Error ? error.message : 'Failed to delete list', 'error');
    } finally {
      setIsDeletingList(false);
    }
  };

  // Format date for display
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Filtered lists and members for selection
  const filteredLists = lists.filter(list =>
    list.name.toLowerCase().includes(listSearchQuery.toLowerCase())
  );

  const filteredMembers = familyMembers.filter(member =>
    member.memberName.toLowerCase().includes(assigneeSearchQuery.toLowerCase())
  );


  if (isLoading && todos.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ height: '100%', minHeight: 0 }}>
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {/* Compact Header */}
        <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-20`}>
          <div className="px-4 py-2">
              <div className="flex items-center justify-between mb-2">
              <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Todos & Lists</h1>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${tokens.text.muted}`}>{sortedAndFiltered.length} total</span>
              </div>
            </div>

            {/* Search */}
            <input
              type="text"
              className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] mb-2`}
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Lists Section - Expandable */}
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowLists(!showLists)}
                  className={`flex-1 flex items-center justify-between px-2 py-1.5 rounded-landing ${tokens.button.secondary} min-h-[36px]`}
                >
                  <div className="flex items-center gap-2">
                    {listFilter !== 'all' ? (
                      <span className="text-base">
                        {listsWithCounts.find(l => l.listId === listFilter)?.icon || '📋'}
                      </span>
                    ) : (
                      <Icon name="checklist" size="sm" />
                    )}
                    <span className={`text-xs font-semibold ${tokens.text.primary}`}>
                      Lists {listFilter !== 'all' && `(${listsWithCounts.find(l => l.listId === listFilter)?.name})`}
                    </span>
                  </div>
                  <Icon name={showLists ? 'chevron-up' : 'chevron-down'} size="sm" />
                </button>
                <button
                  onClick={() => setShowListManagement(true)}
                  className={`px-2 py-1.5 rounded-landing ${tokens.button.secondary} min-h-[36px] flex items-center justify-center gap-1`}
                  title="Manage Lists - Create, edit, or delete lists"
                  aria-label="Manage Lists"
                >
                  <Icon name="settings" size="sm" />
                  <span className="text-xs font-medium">Manage</span>
                </button>
              </div>
              {showLists && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                      listFilter === 'all'
                        ? `${tokens.border.accent} ${tokens.button.secondary}`
                        : `${tokens.border.light} ${tokens.card.bg}`
                    }`}
                    onClick={() => {
                      setListFilter('all');
                      setShowLists(false);
                    }}
                  >
                    <span className="text-lg">📋</span>
                    <span className={`text-xs font-semibold ${tokens.text.primary}`}>All Lists</span>
                    <span className={`text-[10px] ${tokens.text.muted}`}>{todos.length} todos</span>
                  </button>
                  {listsWithCounts.map((list) => (
                    <button
                      key={list.listId}
                      className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                        listFilter === list.listId
                          ? `${tokens.border.accent} ${tokens.button.secondary}`
                          : `${tokens.border.light} ${tokens.card.bg}`
                      }`}
                      onClick={() => {
                        setListFilter(list.listId);
                        setShowLists(false);
                      }}
                    >
                      <span className="text-lg">{list.icon || '📋'}</span>
                      <span className={`text-xs font-semibold ${tokens.text.primary} text-center`}>{list.name}</span>
                      <span className={`text-[10px] ${tokens.text.muted}`}>{list.count} todos</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Message */}
            {filterMessage && (
              <div
                className="fixed top-20 left-1/2 z-50 px-4 py-2 rounded-full shadow-lg pointer-events-none transform -translate-x-1/2 w-auto max-w-[90vw]"
                style={{
                  animation: 'fadeInOut 2s ease-in-out',
                  backgroundColor: isDarkMode ? '#111827' : '#fffefb',
                  color: isDarkMode ? '#e5e7eb' : '#3d3529',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(141, 125, 107, 0.15)'}`,
                }}
              >
                <span className="text-sm font-medium whitespace-nowrap">{filterMessage}</span>
              </div>
            )}

            {/* Quick Filters */}
            <div className="space-y-2">
              {/* Status and Priority Filters - Single Line */}
              <div className="flex gap-2 flex-nowrap items-center">
                {(['open', 'done'] as const).map((filter) => {
                  const isSelected = statusFilter === filter;
                  return (
                    <button
                      key={filter}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 border-2 flex-shrink-0 ${
                        filter === 'open'
                          ? `${isSelected ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-blue-50 text-blue-600 border-blue-200'} ${isSelected ? 'ring-2 ring-offset-1' : ''}`
                          : `${isSelected ? 'bg-green-100 text-green-700 border-green-300' : 'bg-green-50 text-green-600 border-green-200'} ${isSelected ? 'ring-2 ring-offset-1' : ''}`
                      }`}
                      style={{ minHeight: '28px' }}
                      onClick={() => setStatusFilter(isSelected ? null : filter)}
                    >
                      {filter === 'open' ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          <span className="capitalize">Open</span>
                        </>
                      ) : (
                        <>
                          <Icon name="check" size="sm" className="text-green-600" />
                          <span className="capitalize">Done</span>
                        </>
                      )}
                    </button>
                  );
                })}
                {(['critical', 'high', 'medium', 'low'] as const).map((filter) => {
                  const isSelected = priorityFilter === filter;
                  const priorityColors = {
                    critical: { bg: isSelected ? 'bg-red-100' : 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
                    high: { bg: isSelected ? 'bg-orange-100' : 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
                    medium: { bg: isSelected ? 'bg-yellow-100' : 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
                    low: { bg: isSelected ? 'bg-blue-100' : 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-300' },
                  };
                  const colors = priorityColors[filter];
                  const priorityLabels = {
                    critical: 'Critical',
                    high: 'High',
                    medium: 'Medium',
                    low: 'Low',
                  };
                  return (
                    <button
                      key={filter}
                      className={`px-2.5 py-1 rounded-full flex items-center gap-1 border-2 flex-shrink-0 ${colors.bg} ${colors.text} ${colors.border}`}
                      style={{ minHeight: '28px' }}
                      onClick={() => setPriorityFilter(isSelected ? null : filter)}
                      title={filter.charAt(0).toUpperCase() + filter.slice(1)}
                    >
                      <span className={`w-3 h-3 rounded-full ${
                        filter === 'critical' ? 'bg-red-500' :
                        filter === 'high' ? 'bg-orange-500' :
                        filter === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}></span>
                      {isSelected && (
                        <span className="text-xs font-semibold capitalize">{priorityLabels[filter]}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Assignee Filters */}
              <div className="flex gap-2 flex-wrap items-center">
                <span className={`text-xs font-medium ${tokens.text.muted} mr-1`}>Assigned to:</span>
                <button
                  className={`px-2 py-1 rounded-full text-xs font-semibold min-h-[24px] flex items-center justify-center gap-1 border-2 ${
                    assigneeFilter === 'all'
                      ? `${tokens.button.secondary} ${tokens.text.primary} ${tokens.border.light}`
                      : `${tokens.card.bg} ${tokens.text.secondary} ${tokens.border.light}`
                  }`}
                  onClick={() => setAssigneeFilter('all')}
                  style={{ minHeight: '24px' }}
                >
                  <span>All</span>
                </button>
                {familyMembers.map((member) => {
                  const isSelected = assigneeFilter === member.memberId;
                  const memberColor = member.memberColor || '#3b82f6';
                  return (
                    <button
                      key={member.memberId}
                      className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 border-2 ${
                        isSelected
                          ? 'ring-2 ring-offset-1'
                          : ''
                      }`}
                      style={{
                        minHeight: '24px',
                        backgroundColor: isSelected ? `${memberColor}20` : tokens.card.bg,
                        borderColor: isSelected ? memberColor : tokens.border.light,
                        color: isSelected ? memberColor : tokens.text.secondary,
                      }}
                      onClick={() => setAssigneeFilter(member.memberId)}
                    >
                      <MemberAvatar member={member} size="sm" />
                      <span>{member.memberName}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Compact List */}
        <div className="px-3 py-2">
          <div className="space-y-1">
            {visibleTodos.map(todo => {
              const swipeOffset = swipeOffsets[todo.todoId] || 0;
              const priorityColorMap = {
                critical: '#ef4444', // red-500
                high: '#f97316', // orange-500
                medium: '#eab308', // yellow-500
                low: '#3b82f6', // blue-500
              };
              const priorityColor = priorityColorMap[todo.priority || 'medium'];

              return (
                <div key={todo.todoId} className="relative" style={{ touchAction: 'pan-y' }}>
                  {/* Swipe background */}
                  {Math.abs(swipeOffset) > 10 && (
                    <div className="absolute inset-0 flex items-center rounded-landing z-0">
                      {swipeOffset > 0 && todo.status !== 'done' ? (
                        <div className="flex-1 bg-green-500 flex items-center justify-start pl-4 rounded-l-landing">
                          <Icon name="check" size="md" className="text-white" />
                          <span className="ml-2 text-white font-semibold">Mark Done</span>
                        </div>
                      ) : swipeOffset < 0 && todo.status === 'done' ? (
                        <div className="flex-1 bg-blue-500 flex items-center justify-end pr-4 rounded-r-landing">
                          <span className="mr-2 text-white font-semibold">Reopen</span>
                          <Icon name="arrow-left" size="md" className="text-white" />
                        </div>
                      ) : null}
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setSelectedTodo(todo);
                      setShowDetailSheet(true);
                      setEditingField(null);
                      setEditValues({});
                    }}
                    onTouchStart={(e) => handleTouchStart(todo.todoId, e)}
                    onTouchMove={(e) => handleTouchMove(todo.todoId, e)}
                    onTouchEnd={(e) => handleTouchEnd(todo.todoId, e)}
                    className={`w-full ${tokens.card.bg} ${tokens.border.light} border-l-4 rounded-landing p-2 min-h-[44px] flex items-center gap-2 cursor-pointer touch-manipulation transition-transform relative z-10 ${
                      todo.status === 'done' ? 'opacity-60' : ''
                    }`}
                    style={{
                      transform: `translateX(${swipeOffset}px)`,
                      borderLeftColor: todo.status === 'done' ? '#10b981' : priorityColor,
                      willChange: 'transform',
                      transition: Math.abs(swipeOffset) < 5 ? 'transform 0.1s' : 'none',
                    }}
                  >
                    <div className="flex-1 min-w-0 text-left">
                      <div className={`text-sm font-semibold ${tokens.text.primary} ${todo.status === 'done' ? 'line-through' : ''}`}>
                        {todo.title}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {todo.dueDateTime && (
                          <span className={`text-xs ${tokens.text.muted} flex items-center gap-1`}>
                            <Icon name="clock" size="sm" />
                            {formatDate(todo.dueDateTime)}
                          </span>
                        )}
                        {todo.assigneeName && (
                          <span className={`text-xs ${tokens.text.muted} flex items-center gap-1`}>
                            <MemberAvatar
                              member={familyMembers.find(m => m.memberId === todo.assigneeId) || { memberId: todo.assigneeId || '', memberName: todo.assigneeName || '', memberColor: '#3b82f6' }}
                              size="sm"
                            />
                            {todo.assigneeName}
                          </span>
                        )}
                        {todo.listId && (
                          <span className={`text-xs ${tokens.text.muted}`}>
                            {lists.find(l => l.listId === todo.listId)?.name}
                          </span>
                        )}
                      </div>
                    </div>
                    {todo.status === 'done' && (
                      <Icon name="check" size="sm" className="text-green-500" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Load More */}
          {hasMore && (
            <button
              onClick={() => setVisibleCount(prev => prev + 30)}
              className={`w-full mt-3 py-2 rounded-landing ${tokens.button.secondary} ${tokens.text.primary} font-semibold`}
            >
              Load More ({sortedAndFiltered.length - visibleCount} remaining)
            </button>
          )}
        </div>
      </div>

      {/* Todo Detail Bottom Sheet */}
      {selectedTodo && (
        <BottomSheet
          isOpen={showDetailSheet}
          onClose={() => {
            setShowDetailSheet(false);
            setSelectedTodo(null);
            setEditingField(null);
            setEditValues({});
          }}
          title="Todo Details"
        >
          <div className="p-3 space-y-4">
            {/* Title */}
            <div className="relative">
              {editingField === 'title' ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editValues.title ?? selectedTodo.title}
                    onChange={(e) => setEditValues(prev => ({ ...prev, title: e.target.value }))}
                    className={`flex-1 px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        confirmFieldEdit('title');
                      }
                    }}
                  />
                  <button
                    onClick={() => confirmFieldEdit('title')}
                    className={`w-10 h-10 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform`}
                  >
                    <Icon name="check" size="sm" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('title');
                    setEditValues(prev => ({ ...prev, title: selectedTodo.title }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <span className={`font-semibold ${tokens.text.primary}`}>{selectedTodo.title}</span>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* Description */}
            {editingField === 'description' ? (
              <div className="flex items-start gap-2">
                <textarea
                  value={editValues.description ?? selectedTodo.description ?? ''}
                  onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                  className={`flex-1 px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[80px]`}
                  autoFocus
                />
                <button
                  onClick={() => confirmFieldEdit('description')}
                  className={`w-10 h-10 rounded-full ${tokens.button.primary} flex items-center justify-center touch-manipulation active:scale-95 transition-transform`}
                >
                  <Icon name="check" size="sm" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => {
                  setEditingField('description');
                  setEditValues(prev => ({ ...prev, description: selectedTodo.description }));
                }}
                className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] cursor-pointer`}
              >
                <span className={`${tokens.text.secondary} ${!selectedTodo.description ? 'italic' : ''}`}>
                  {selectedTodo.description || 'Tap to add description'}
                </span>
                <Icon name="edit" size="sm" className={`${tokens.text.muted} float-right mt-1`} />
              </div>
            )}

            {/* Priority */}
            <div className="relative">
              {editingField === 'priority' ? (
                <div className="space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    {(['critical', 'high', 'medium', 'low'] as TodoPriority[]).map((priority) => (
                      <button
                        key={priority}
                        onClick={() => {
                          setEditValues(prev => ({ ...prev, priority }));
                          confirmFieldEdit('priority');
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-semibold min-h-[44px] border-2 ${
                          editValues.priority === priority || (!editValues.priority && selectedTodo.priority === priority)
                            ? priority === 'critical' ? 'bg-red-100 text-red-700 border-red-300' :
                              priority === 'high' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                              priority === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                              'bg-blue-100 text-blue-600 border-blue-300'
                            : `${tokens.card.bg} ${tokens.text.secondary} ${tokens.border.light}`
                        }`}
                      >
                        <span className="capitalize">{priority}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('priority');
                    setEditValues(prev => ({ ...prev, priority: selectedTodo.priority }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${
                      selectedTodo.priority === 'critical' ? 'bg-red-500' :
                      selectedTodo.priority === 'high' ? 'bg-orange-500' :
                      selectedTodo.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></span>
                    <span className={`font-semibold capitalize ${tokens.text.primary}`}>
                      {selectedTodo.priority || 'medium'}
                    </span>
                  </div>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* Status */}
            <div className="relative">
              {editingField === 'status' ? (
                <div className="flex gap-2">
                  {(['open', 'done'] as TodoStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setEditValues(prev => ({ ...prev, status }));
                        confirmFieldEdit('status');
                      }}
                      className={`flex-1 px-4 py-2 rounded-full text-sm font-semibold min-h-[44px] border-2 ${
                        editValues.status === status || (!editValues.status && selectedTodo.status === status)
                          ? status === 'done' ? 'bg-green-100 text-green-700 border-green-300' : `${tokens.button.secondary} ${tokens.text.primary} ${tokens.border.light}`
                          : `${tokens.card.bg} ${tokens.text.secondary} ${tokens.border.light}`
                      }`}
                    >
                      <span className="capitalize">{status}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('status');
                    setEditValues(prev => ({ ...prev, status: selectedTodo.status }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <span className={`font-semibold capitalize ${tokens.text.primary}`}>
                    {selectedTodo.status === 'done' ? '✓ Done' : '○ Open'}
                  </span>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* Due Date */}
            <div className="relative">
              {editingField === 'dueDate' ? (
                <div className="space-y-2">
                  <input
                    type="date"
                    value={editValues.dueDateTime instanceof Date
                      ? editValues.dueDateTime.toISOString().split('T')[0]
                      : editValues.dueDateTime
                        ? typeof editValues.dueDateTime === 'string' && editValues.dueDateTime.match(/^\d{4}-\d{2}-\d{2}$/)
                          ? editValues.dueDateTime
                          : new Date(editValues.dueDateTime).toISOString().split('T')[0]
                        : selectedTodo.dueDateTime
                          ? selectedTodo.dueDateTime instanceof Date
                            ? selectedTodo.dueDateTime.toISOString().split('T')[0]
                            : new Date(selectedTodo.dueDateTime).toISOString().split('T')[0]
                          : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined;
                      setEditValues(prev => ({ ...prev, dueDateTime: date }));
                    }}
                    className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => confirmFieldEdit('dueDate')}
                      className={`flex-1 px-4 py-2 rounded-landing ${tokens.button.primary} font-semibold min-h-[44px]`}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditValues(prev => ({ ...prev, dueDateTime: undefined }));
                        confirmFieldEdit('dueDate');
                      }}
                      className={`px-4 py-2 rounded-landing ${tokens.button.secondary} ${tokens.text.primary} font-semibold min-h-[44px]`}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('dueDate');
                    setEditValues(prev => ({ ...prev, dueDateTime: selectedTodo.dueDateTime }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="clock" size="sm" className={tokens.text.muted} />
                    <span className={`${tokens.text.secondary} ${!selectedTodo.dueDateTime ? 'italic' : ''}`}>
                      {selectedTodo.dueDateTime ? formatDate(selectedTodo.dueDateTime) : 'Tap to set due date'}
                    </span>
                  </div>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* Assignee */}
            <div className="relative">
              {editingField === 'assignee' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={assigneeSearchQuery}
                    onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                    className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] mb-2`}
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditValues(prev => ({ ...prev, assigneeId: 'family', assigneeName: 'Family' }));
                        confirmFieldEdit('assignee');
                        setAssigneeSearchQuery('');
                      }}
                      className={`w-full p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center gap-2 ${
                        editValues.assigneeId === 'family' || (!editValues.assigneeId && selectedTodo.isFamily)
                          ? `${tokens.button.secondary} ${tokens.border.accent}`
                          : ''
                      }`}
                    >
                      <Icon name="family" size="sm" />
                      <span className={`font-semibold ${tokens.text.primary}`}>Family</span>
                    </button>
                    {filteredMembers.map((member) => (
                      <button
                        key={member.memberId}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditValues(prev => ({ ...prev, assigneeId: member.memberId, assigneeName: member.memberName }));
                          confirmFieldEdit('assignee');
                          setAssigneeSearchQuery('');
                        }}
                        className={`w-full p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center gap-2 ${
                          editValues.assigneeId === member.memberId || (!editValues.assigneeId && selectedTodo.assigneeId === member.memberId)
                            ? `${tokens.button.secondary} ${tokens.border.accent}`
                            : ''
                        }`}
                      >
                        <MemberAvatar member={member} size="sm" />
                        <span className={`font-semibold ${tokens.text.primary}`}>{member.memberName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('assignee');
                    setEditValues(prev => ({
                      ...prev,
                      assigneeId: selectedTodo.isFamily ? 'family' : selectedTodo.assigneeId,
                      assigneeName: selectedTodo.isFamily ? 'Family' : selectedTodo.assigneeName,
                    }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <div className="flex items-center gap-2">
                    {selectedTodo.isFamily ? (
                      <>
                        <Icon name="family" size="sm" />
                        <span className={`font-semibold ${tokens.text.primary}`}>Family</span>
                      </>
                    ) : selectedTodo.assigneeName ? (
                      <>
                        <MemberAvatar
                          member={familyMembers.find(m => m.memberId === selectedTodo.assigneeId) || { memberId: selectedTodo.assigneeId || '', memberName: selectedTodo.assigneeName || '', memberColor: '#3b82f6' }}
                          size="sm"
                        />
                        <span className={`font-semibold ${tokens.text.primary}`}>{selectedTodo.assigneeName}</span>
                      </>
                    ) : (
                      <span className={`${tokens.text.muted} italic`}>Tap to assign</span>
                    )}
                  </div>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* List */}
            <div className="relative">
              {editingField === 'list' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Search lists..."
                    value={listSearchQuery}
                    onChange={(e) => setListSearchQuery(e.target.value)}
                    className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[40px] mb-2`}
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmFieldEdit('list', undefined);
                        setListSearchQuery('');
                      }}
                      className={`w-full p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center gap-2 ${
                        !editValues.listId && !selectedTodo.listId
                          ? `${tokens.button.secondary} ${tokens.border.accent}`
                          : ''
                      }`}
                    >
                      <span className="text-lg">📋</span>
                      <span className={`font-semibold ${tokens.text.primary}`}>No List</span>
                    </button>
                    {filteredLists.map((list) => (
                      <button
                        key={list.listId}
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmFieldEdit('list', list.listId);
                          setListSearchQuery('');
                        }}
                        className={`w-full p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center gap-2 ${
                          editValues.listId === list.listId || (!editValues.listId && selectedTodo.listId === list.listId)
                            ? `${tokens.button.secondary} ${tokens.border.accent}`
                            : ''
                        }`}
                      >
                        <span className="text-lg">{list.icon || '📋'}</span>
                        <span className={`font-semibold ${tokens.text.primary}`}>{list.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingField('list');
                    setEditValues(prev => ({ ...prev, listId: selectedTodo.listId }));
                  }}
                  className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border min-h-[44px] flex items-center justify-between cursor-pointer`}
                >
                  <div className="flex items-center gap-2">
                    {selectedTodo.listId ? (
                      <>
                        <span className="text-lg">{lists.find(l => l.listId === selectedTodo.listId)?.icon || '📋'}</span>
                        <span className={`font-semibold ${tokens.text.primary}`}>
                          {lists.find(l => l.listId === selectedTodo.listId)?.name}
                        </span>
                      </>
                    ) : (
                      <span className={`${tokens.text.muted} italic`}>Tap to assign to list</span>
                    )}
                  </div>
                  <Icon name="edit" size="sm" className={tokens.text.muted} />
                </div>
              )}
            </div>

            {/* Linked Event */}
            {selectedTodo.eventId && selectedTodo.eventTitle && (
              <div className={`p-3 rounded-landing ${tokens.card.bg} ${tokens.border.light} border`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="calendar" size="sm" className={tokens.text.muted} />
                  <span className={`text-xs font-semibold ${tokens.text.muted}`}>Linked to Event</span>
                </div>
                <span className={`font-semibold ${tokens.text.primary}`}>{selectedTodo.eventTitle}</span>
              </div>
            )}

            {/* Delete Button */}
            <button
              onClick={() => handleDeleteTodo(selectedTodo.todoId)}
              disabled={isDeletingTodo}
              className={`w-full py-3 rounded-landing ${tokens.button.secondary} ${tokens.text.primary} font-semibold min-h-[48px] flex items-center justify-center gap-2 ${
                isDeletingTodo ? 'opacity-50' : ''
              }`}
            >
              {isDeletingTodo ? (
                <>
                  <LoadingSpinner />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Icon name="delete" size="sm" />
                  <span>Delete Todo</span>
                </>
              )}
            </button>
          </div>
        </BottomSheet>
      )}

      {/* List Management Bottom Sheet */}
      <BottomSheet
        isOpen={showListManagement}
        onClose={() => setShowListManagement(false)}
        title="Manage Lists"
      >
        <div className="flex flex-col h-full">
          <div className="p-3 pb-2 flex-shrink-0">
            <button
              onClick={() => {
                setNewListName('');
                setNewListIcon('');
                setShowCreateListSheet(true);
              }}
              className={`w-full py-3 rounded-landing ${tokens.button.primary} font-semibold min-h-[48px] flex items-center justify-center gap-2`}
            >
              <Icon name="plus" size="sm" />
              <span>Create New List</span>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
            <div className="grid grid-cols-2 gap-2">
            {lists.map((list) => {
              const count = todos.filter(t => t.listId === list.listId).length;
              return (
                <div
                  key={list.listId}
                  className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 relative ${tokens.border.light} ${tokens.card.bg}`}
                >
                  <span className="text-lg">{list.icon || '📋'}</span>
                  <span className={`text-xs font-semibold ${tokens.text.primary} text-center`}>{list.name}</span>
                  <span className={`text-[10px] ${tokens.text.muted}`}>{count} todos</span>
                  <div className="absolute top-1 right-1 flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingList(list);
                        setEditListName(list.name);
                        setEditListIcon(list.icon || '');
                        setShowEditListSheet(true);
                      }}
                      className={`w-6 h-6 rounded-full ${tokens.button.secondary} ${tokens.text.primary} flex items-center justify-center touch-manipulation active:scale-95`}
                      title="Edit list"
                    >
                      <Icon name="edit" size="sm" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteList(list.listId);
                      }}
                      disabled={isDeletingList}
                      className={`w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center touch-manipulation active:scale-95 ${
                        isDeletingList ? 'opacity-50' : ''
                      }`}
                      title="Delete list"
                    >
                      <Icon name="delete" size="sm" />
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      </BottomSheet>

      {/* Create List Bottom Sheet */}
      <BottomSheet
        isOpen={showCreateListSheet}
        onClose={() => {
          setShowCreateListSheet(false);
          setNewListName('');
          setNewListIcon('');
        }}
        title="Create List"
      >
        <div className="p-3 space-y-4">
          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>List Name</label>
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
              placeholder="Enter list name"
              autoFocus
            />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Icon (emoji)</label>
            <input
              type="text"
              value={newListIcon}
              onChange={(e) => setNewListIcon(e.target.value)}
              className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
              placeholder="📋"
              maxLength={2}
            />
          </div>
          <button
            onClick={handleCreateList}
            disabled={isCreatingList || !newListName.trim()}
            className={`w-full py-3 rounded-landing ${tokens.button.primary} font-semibold min-h-[48px] flex items-center justify-center gap-2 ${
              isCreatingList || !newListName.trim() ? 'opacity-50' : ''
            }`}
          >
            {isCreatingList ? (
              <>
                <LoadingSpinner />
                <span>Creating...</span>
              </>
            ) : (
              <span>Create List</span>
            )}
          </button>
        </div>
      </BottomSheet>

      {/* Edit List Bottom Sheet */}
      {editingList && (
        <BottomSheet
          isOpen={showEditListSheet}
          onClose={() => {
            setShowEditListSheet(false);
            setEditingList(null);
            setEditListName('');
            setEditListIcon('');
          }}
          title="Edit List"
        >
          <div className="p-3 space-y-4">
            <div>
              <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>List Name</label>
              <input
                type="text"
                value={editListName}
                onChange={(e) => setEditListName(e.target.value)}
                className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                placeholder="Enter list name"
                autoFocus
              />
            </div>
            <div>
              <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Icon (emoji)</label>
              <input
                type="text"
                value={editListIcon}
                onChange={(e) => setEditListIcon(e.target.value)}
                className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                placeholder="📋"
                maxLength={2}
              />
            </div>
            <button
              onClick={handleUpdateList}
              disabled={isUpdatingList || !editListName.trim()}
              className={`w-full py-3 rounded-landing ${tokens.button.primary} font-semibold min-h-[48px] flex items-center justify-center gap-2 ${
                isUpdatingList || !editListName.trim() ? 'opacity-50' : ''
              }`}
            >
              {isUpdatingList ? (
                <>
                  <LoadingSpinner />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Update List</span>
              )}
            </button>
          </div>
        </BottomSheet>
      )}

      {/* Floating Action Button - Create Todo */}
      <button
        className={`fixed right-4 w-14 h-14 ${tokens.button.primary} shadow-lg z-30 active:scale-95 min-h-[56px] min-w-[56px] rounded-full flex items-center justify-center touch-manipulation`}
        style={{ 
          bottom: 'calc(56px + 16px + env(safe-area-inset-bottom, 0px))'
        }}
        onClick={() => {
          setNewTodoTitle('');
          setNewTodoDescription('');
          setNewTodoDueDate('');
          setNewTodoPriority('medium');
          setNewTodoAssignee(currentUserId || 'family');
          setNewTodoListId(listFilter !== 'all' ? listFilter : '');
          setShowCreateTodoSheet(true);
        }}
        aria-label="Create todo"
      >
        <Icon name="plus" size="md" className="text-white" />
      </button>

      {/* Create Todo Bottom Sheet */}
      <BottomSheet
        isOpen={showCreateTodoSheet}
        onClose={() => {
          setShowCreateTodoSheet(false);
          setNewTodoTitle('');
          setNewTodoDescription('');
          setNewTodoDueDate('');
          setNewTodoPriority('medium');
          setNewTodoAssignee('family');
          setNewTodoListId('');
        }}
        title="Create Todo"
      >
        <div className="p-3 space-y-4">
          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Title *</label>
            <input
              type="text"
              value={newTodoTitle}
              onChange={(e) => setNewTodoTitle(e.target.value)}
              className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
              placeholder="Todo title"
              autoFocus
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Description</label>
            <textarea
              value={newTodoDescription}
              onChange={(e) => setNewTodoDescription(e.target.value)}
              className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[100px]`}
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Due Date</label>
            <input
              type="date"
              value={newTodoDueDate}
              onChange={(e) => setNewTodoDueDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-landing ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Priority</label>
            <div className="flex gap-2 flex-wrap">
              {(['critical', 'high', 'medium', 'low'] as const).map((priority) => {
                const isSelected = newTodoPriority === priority;
                const priorityColors = {
                  critical: { bg: isSelected ? 'bg-red-100' : 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
                  high: { bg: isSelected ? 'bg-orange-100' : 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
                  medium: { bg: isSelected ? 'bg-yellow-100' : 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
                  low: { bg: isSelected ? 'bg-blue-100' : 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-300' },
                };
                const colors = priorityColors[priority];
                const priorityLabels = {
                  critical: 'Critical',
                  high: 'High',
                  medium: 'Medium',
                  low: 'Low',
                };
                return (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => setNewTodoPriority(priority)}
                    className={`px-2.5 py-1 rounded-full flex items-center gap-1 border-2 flex-shrink-0 ${colors.bg} ${colors.text} ${colors.border}`}
                    style={{ minHeight: '28px' }}
                  >
                    <span className={`w-3 h-3 rounded-full ${
                      priority === 'critical' ? 'bg-red-500' :
                      priority === 'high' ? 'bg-orange-500' :
                      priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></span>
                    <span className="text-xs font-semibold capitalize">{priorityLabels[priority]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>Assign To</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setNewTodoAssignee('family')}
                className={`px-3 py-2 rounded-landing border-2 min-h-[44px] flex items-center gap-2 ${
                  newTodoAssignee === 'family'
                    ? `${tokens.border.accent} ${tokens.button.secondary}`
                    : `${tokens.border.light} ${tokens.card.bg}`
                }`}
              >
                <span className="text-base">👥</span>
                <span className={`text-xs font-semibold ${tokens.text.primary}`}>Family</span>
              </button>
              {familyMembers.map((member) => {
                const isSelected = newTodoAssignee === member.memberId;
                return (
                  <button
                    key={member.memberId}
                    type="button"
                    onClick={() => setNewTodoAssignee(member.memberId)}
                    className={`px-3 py-2 rounded-landing border-2 min-h-[44px] flex items-center gap-2 ${
                      isSelected
                        ? `${tokens.border.accent} ${tokens.button.secondary}`
                        : `${tokens.border.light} ${tokens.card.bg}`
                    }`}
                  >
                    <MemberAvatar member={member} size="sm" />
                    <span className={`text-xs font-semibold ${tokens.text.primary}`}>{member.memberName}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`}>List (optional)</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setNewTodoListId('')}
                className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                  !newTodoListId
                    ? `${tokens.border.accent} ${tokens.button.secondary}`
                    : `${tokens.border.light} ${tokens.card.bg}`
                }`}
              >
                <span className="text-lg">📋</span>
                <span className={`text-xs font-semibold ${tokens.text.primary} text-center`}>No List</span>
              </button>
              {listsWithCounts.map((list) => (
                <button
                  key={list.listId}
                  type="button"
                  onClick={() => setNewTodoListId(list.listId)}
                  className={`p-3 rounded-landing border-2 min-h-[60px] flex flex-col items-center justify-center gap-1 ${
                    newTodoListId === list.listId
                      ? `${tokens.border.accent} ${tokens.button.secondary}`
                      : `${tokens.border.light} ${tokens.card.bg}`
                  }`}
                >
                  <span className="text-lg">{list.icon || '📋'}</span>
                  <span className={`text-xs font-semibold ${tokens.text.primary} text-center`}>{list.name}</span>
                  <span className={`text-[10px] ${tokens.text.muted}`}>{list.count} todos</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleCreateTodo}
            disabled={isCreatingTodo || !newTodoTitle.trim()}
            className={`w-full py-3 rounded-landing ${tokens.button.primary} font-semibold min-h-[48px] flex items-center justify-center gap-2 ${
              isCreatingTodo || !newTodoTitle.trim() ? 'opacity-50' : ''
            }`}
          >
            {isCreatingTodo ? (
              <>
                <LoadingSpinner />
                <span>Creating...</span>
              </>
            ) : (
              <span>Create Todo</span>
            )}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
